import React, { useEffect, useState } from "react";
import { roleLabel } from "../../OffSecStaffPortal/offsecStaffConstants";
import axios from "axios";
import "./OffSecStaff.css";

const API_URL = process.env.REACT_APP_API_URL;

const OffSecStaffMapping = () => {
  const [staffUsers, setStaffUsers] = useState([]);
  const [caraAdmins, setCaraAdmins] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [statusValue, setStatusValue] = useState("active");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const [staffRes, adminRes, mapRes] = await Promise.all([
        axios.get(`${API_URL}api/offsec/staff/`, { withCredentials: true }),
        axios.get(`${API_URL}api/offsec/admins/`, { withCredentials: true }),
        axios.get(`${API_URL}api/offsec/mappings/`, { withCredentials: true }),
      ]);

      setStaffUsers(staffRes.data?.offsec_staff_users || []);
      setCaraAdmins(adminRes.data?.admins || []);
      setMappings(mapRes.data?.mappings || []);
    } catch (error) {
      setMessage("Failed to load OffSec Team mapping data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!selectedStaff || !selectedAdmin) {
      setMessage("Please select both an OffSec Team member and CARA Admin.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}api/offsec/mappings/create/`,
        {
          offsec_staff_user_id: selectedStaff,
          admin_id: selectedAdmin,
          status: statusValue,
        },
        { withCredentials: true }
      );
      setMessage("Mapping saved successfully.");
      await loadData();
    } catch (error) {
      setMessage("Failed to save mapping.");
    }
  };

  const handleToggleStatus = async (mapping) => {
    const newStatus = String(mapping.status).toLowerCase() === "active" ? "inactive" : "active";
    try {
      await axios.post(
        `${API_URL}api/offsec/mappings/status/`,
        {
          offsec_staff_user_id: mapping.offsec_staff_user_id,
          admin_id: mapping.admin_id,
          status: newStatus,
        },
        { withCredentials: true }
      );
      await loadData();
    } catch (error) {
      setMessage("Failed to update mapping status.");
    }
  };

  return (
    <div className="content-wrapper">
      <main id="offsec_staff_mapping" className="offsec-staff-mapping-page">
        <div className="offsec-staff-mapping-header">
          <div>
            <h4>OffSec Team Mapping</h4>
            <p>Assign OffSec Team members to CARA Admin companies whose Service Requests they can review.</p>
          </div>
        </div>

        {message && <div className="offsec-staff-mapping-message">{message}</div>}

        <form className="offsec-staff-mapping-form" onSubmit={handleCreate}>
          <div className="offsec-staff-mapping-field">
            <label htmlFor="staffSelect">OffSec Team</label>
            <select
              id="staffSelect"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              <option value="">Select OffSec Team</option>
              {staffUsers.map((staff) => (
                <option key={staff.offsec_staff_user_id} value={staff.offsec_staff_user_id}>
                  {`${staff.first_name} ${staff.last_name}`} ({staff.email}) — {roleLabel(staff.roleName || 'Reviewer')}
                </option>
              ))}
            </select>
          </div>

          <div className="offsec-staff-mapping-field">
            <label htmlFor="adminSelect">CARA Admin</label>
            <select
              id="adminSelect"
              value={selectedAdmin}
              onChange={(e) => setSelectedAdmin(e.target.value)}
            >
              <option value="">Select CARA Admin</option>
              {caraAdmins.map((admin) => (
                <option key={admin.admin_id} value={admin.admin_id}>
                  {admin.company_name || `${admin.first_name} ${admin.last_name}`}
                </option>
              ))}
            </select>
          </div>

          <div className="offsec-staff-mapping-field">
            <label htmlFor="statusSelect">Status</label>
            <select
              id="statusSelect"
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <button type="submit" className="offsec-staff-mapping-button">
            Save Mapping
          </button>
        </form>

        <div className="offsec-staff-mapping-table-wrapper">
          {loading ? (
            <div className="offsec-staff-mapping-message">Loading mappings...</div>
          ) : (
            <table className="offsec-staff-mapping-table">
              <thead>
                <tr>
                  <th>OffSec Team</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Tenant DB</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {mappings.length === 0 && (
                  <tr>
                    <td colSpan="6">No mappings found.</td>
                  </tr>
                )}
                {mappings.map((mapping) => (
                  <tr key={`${mapping.offsec_staff_user_id}-${mapping.admin_id}`}>
                    <td>{mapping.offsec_staff_name || mapping.offsec_staff_user_id}</td>
                    <td>{roleLabel(mapping.offsec_staff_role || "Reviewer")}</td>
                    <td>{mapping.company_name || mapping.admin_id}</td>
                    <td>{mapping.tenant_db_name || "--"}</td>
                    <td className={mapping.status === "active" ? "status-active" : "status-inactive"}>
                      {mapping.status}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="offsec-staff-mapping-action"
                        onClick={() => handleToggleStatus(mapping)}
                      >
                        {String(mapping.status).toLowerCase() === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default OffSecStaffMapping;
